/* eslint-disable @typescript-eslint/no-explicit-any */
import { successNotification, warningNotification } from "@app/common/components/notifications";
import { IFiltersPagination, IResponse } from "@app/common/types/http";
import { useAuthentication } from "@app/store/useAuthtentication";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  bankListService,
  confirmInventoryStock,
  deliveryDispenseService,
  deliveryPdfDetail,
  deliveryReminderService,
  documentListService,
  inventoryStockService,
  listOrdersService,
  orderDetail,
  paymentListService,
  pricePatientService,
  topInventoryService,
  updateMedicineLotService,
  walletBalanceService
} from "./drugDelivery";
import {
  DeliveryPayload,
  InventoryStockConfirmPayload,
  InventoryStockFilters,
  OrderListFilters,
  PricePatientPayload,
  reminderDispensationPayload,
  TopInventoryFilters,
  WalletBalanceFilters
} from "./types";
import { AxiosError } from "axios";

export function useGetOrdersList(filters: OrderListFilters & IFiltersPagination, enabled = true) {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const query = useQuery({
    enabled,
    gcTime: 0,
    queryKey: [
      "orders",
      filters.page,
      filters.startDate,
      filters.endDate,
      filters.site,
      filters.doctor,
      filters.corporateClient
    ],
    queryFn: async () => await listOrdersService(filters, token)
  });

  return query;
}
export function useGetOrderDetail(filters: { disId?: number; orderId?: number }, enabled = true) {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const query = useQuery({
    enabled,
    gcTime: 0,
    queryKey: ["ordersDetail", filters.orderId, filters.disId],
    queryFn: async () => await orderDetail(filters, token)
  });

  return query;
}

export function useGetPaymentList(filters: { eaccount: number }, enabled = true) {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const query = useQuery({
    enabled,
    gcTime: 0,
    queryKey: ["paymentList", filters.eaccount],
    queryFn: async () => await paymentListService(filters, token)
  });

  return query;
}
export function useGetBankList(filters: { eaccount: number }, enabled = true) {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const query = useQuery({
    enabled,
    gcTime: 0,
    queryKey: ["bankList", filters.eaccount],
    queryFn: async () => await bankListService(filters, token)
  });

  return query;
}
export function useGetDocumentList(enabled = true) {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const query = useQuery({
    enabled,
    gcTime: 0,
    queryKey: ["documentList"],
    queryFn: async () => await documentListService(token)
  });

  return query;
}

export function useGetPatientPrice(
  onSuccess = async (res: IResponse) => {
    res;
  }
) {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);
  const mutation = useMutation({
    mutationFn: async (payload: PricePatientPayload) => await pricePatientService(payload, token),
    onSuccess: async (res) => {
      await onSuccess(res);
    },
    onError: async (error: any) => {
      warningNotification(error.response.data.message, "Intenta de nuevo");
    }
  });

  return mutation;
}

export function useDeliveryDispense(
  onSuccess = async (res: IResponse) => {
    res;
  }
) {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);
  const mutation = useMutation({
    mutationFn: async (payload: DeliveryPayload) => await deliveryDispenseService(payload, token),
    onSuccess: (res) => {
      successNotification(res.message, "Realizado con éxito").finally(() => {
        onSuccess(res);
      });
    },
    onError: async (error: any) => {
      warningNotification(error.response.data.message, "Intenta de nuevo");
    }
  });

  return mutation;
}

export function useGetPdfDetail(filters: { disId: number }, enabled = true) {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const query = useQuery({
    enabled,
    gcTime: 0,
    queryKey: ["dispensaryDetailPdf"],
    queryFn: async () => await deliveryPdfDetail(filters, token)
  });

  return query;
}

export function useDeliveryReminder(
  onSuccess = async (res: IResponse) => {
    res;
  }
) {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);
  const mutation = useMutation({
    mutationFn: async (payload: reminderDispensationPayload) =>
      await deliveryReminderService(payload, token),
    onSuccess: async (res) => {
      successNotification(res.message, "Realizado con éxito");
      await onSuccess(res);
    },
    onError: async (error: any) => {
      warningNotification(error.response.data.message, "Intenta de nuevo");
    }
  });

  return mutation;
}

export function useUpdateMedicineLot(onSuccess: (res: IResponse) => Promise<void>) {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);
  const mutation = useMutation({
    mutationFn: async (payload: { dispensaryId: number; medicineId: number; lot: string }) =>
      await updateMedicineLotService(payload.dispensaryId, payload.medicineId, payload.lot, token),
    onSuccess: async (res) => {
      successNotification("Lote actualizado correctamente", "Realizado con éxito");
      await onSuccess(res);
    },
    onError: async (error: any) => {
      warningNotification(
        error.response?.data?.message || "Error al actualizar el lote",
        "Intenta de nuevo"
      );
    }
  });

  return mutation;
}

export function useGetInventoryStock(filters: InventoryStockFilters, enabled = true) {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const query = useQuery({
    enabled,
    gcTime: 0,
    queryKey: ["inventoryStock", filters.barcode, filters.sedeId, filters.medicineId],
    queryFn: async () => {
      try {
        return await inventoryStockService(filters, token);
      } catch (error) {
        const axiosError = error as AxiosError<{ message: string }>;
        if (axiosError.response?.status === 404) {
          warningNotification("Artículo no encontrado para este código de barras");
        } else {
          warningNotification(axiosError.response?.data?.message);
        }
        throw error;
      }
    }
  });

  return query;
}

export function useGetTopInventory(filters: TopInventoryFilters, enabled = true) {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const query = useQuery({
    enabled: enabled && !!(filters.barcode || filters.articleName) && filters.siteId > 0,
    gcTime: 0,
    queryKey: ["topInventory", filters.barcode, filters.articleName, filters.siteId],
    queryFn: async () => {
      try {
        return await topInventoryService(filters, token);
      } catch (error) {
        const axiosError = error as AxiosError<{ message: string }>;
        warningNotification(axiosError.response?.data?.message);
        throw error;
      }
    }
  });

  return query;
}

export function useConfirmInventoryStock(onSuccess?: () => void) {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const mutation = useMutation({
    mutationFn: async (payload: InventoryStockConfirmPayload) =>
      await confirmInventoryStock(payload, token),
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error: AxiosError<{ message: string }>) => {
      warningNotification(error.response?.data?.message ?? "Error al confirmar dispensación");
    }
  });

  return mutation;
}

export function useGetWalletBalance(filters: WalletBalanceFilters, enabled = true) {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const query = useQuery({
    enabled,
    gcTime: 0,
    queryKey: ["walletBalance", filters.patientId, filters.isExternalPatient],
    queryFn: async () => await walletBalanceService(filters, token)
  });

  return query;
}
